/* global window, document */

(function (global) {
    "use strict";

    /**
     * Thin wrapper around SweetAlert2 to standardise usage across Core.
     * Expects SweetAlert2 to be available on `window.swal` (or `window.Swal` as fallback).
     */
    class Popup {
        constructor() {
            throw new Error("Popup is a static class and cannot be instantiated.");
        }

        /**
         * @returns {*} SweetAlert2 instance.
         * @throws {Error} If SweetAlert2 is not available on window.
         */
        static get swal() {
            var s = (global && global.swal) ? global.swal : (global && global.Swal ? global.Swal : null);
            if (!s) throw new Error("SweetAlert2 is not available. Expected window.swal (or window.Swal).");
            return s;
        }

        /**
         * Fires a SweetAlert2 modal.
         * @param {Object} options SweetAlert2 options.
         * @returns {Promise<*>} SweetAlert2 result promise.
         */
        static fire(options) {
            return Popup.swal.fire(options || {});
        }

        /**
         * Closes the currently open SweetAlert2 modal.
         * @returns {void}
         */
        static close() {
            var s = Popup.swal;
            if (s && typeof s.close === "function") s.close();
        }

        /**
         * Convenience for iframe popups.
         * @param {string} url URL to load.
         * @param {Object} [options] Additional SweetAlert2 options.
         * @param {Object} [frame] Iframe config.
         * @param {string|number} [frame.width="100%"] Iframe width.
         * @param {string|number} [frame.height="80vh"] Iframe height.
         * @param {boolean} [frame.scrolling=false] Whether to allow scrolling in the Swal2 HTML container.
         * @returns {Promise<*>}
         */
        static iframe(url, options, frame) {
            options = options || {};
            frame = frame || {};

            var width = (typeof frame.width === "undefined") ? "100%" : frame.width;
            var height = (typeof frame.height === "undefined") ? "80vh" : frame.height;
            var scrolling = !!frame.scrolling;

            var safeUrl = String(url).replace(/\\/g, "\\\\").replace(/"/g, '\\"');

            var maxHeightCss = (String(height) === "auto") ? "" : ("max-height:" + String(height) + ";");

            var html =
                '<div data-swal-size="true" style="' + maxHeightCss + '">' +
                '<iframe src="' + safeUrl + '" frameborder="0" scrolling="' + (scrolling ? "yes" : "auto") + '" ' +
                'style="width:100%; height:100%; min-height:200px;"></iframe>' +
                "</div>";

            var prefersReducedMotion = !!(global.matchMedia && global.matchMedia("(prefers-reduced-motion: reduce)").matches);
            var showClass = prefersReducedMotion ? { popup: "" } : { popup: "swal2-show" };
            var hideClass = prefersReducedMotion ? { popup: "" } : { popup: "swal2-hide" };

            var didOpen = function (el) {
                if (options.background) {
                    var p = el.querySelector(".swal2-popup");
                    if (p) p.style.background = String(options.background);
                }
                if (scrolling) {
                    var c = el.querySelector(".swal2-html-container");
                    if (c) c.style.overflowY = "auto";
                }
                if (typeof options.didOpen === "function") options.didOpen(el);
            };

            var didClose = function () {
                if (options.background) {
                    var p = document.querySelector(".swal2-popup");
                    if (p) p.style.background = "";
                }
                if (typeof options.didClose === "function") options.didClose();
            };

            var merged = Popup._merge(options, {
                html: html,
                width: width,
                showCloseButton: true,
                closeButtonAriaLabel: "Close dialog",
                showConfirmButton: false,
                buttonsStyling: true,
                allowOutsideClick: true,
                allowEscapeKey: true,
                showClass: showClass,
                hideClass: hideClass,
                didOpen: didOpen,
                didClose: didClose
            });

            return Popup.fire(merged);
        }

        /**
         * Fires an inline modal by copying HTML from a source element into the Swal container.
         * @param {string} sourceId Element id whose innerHTML will be copied.
         * @param {Object} [options] SweetAlert2 options.
         * @param {Object} [layout] Layout options.
         * @param {string|number} [layout.width="auto"] Swal width.
         * @param {string|number} [layout.height="auto"] Max height style.
         * @param {boolean} [layout.scrolling=false] Whether to allow scrolling in the Swal2 HTML container.
         * @returns {Promise<*>}
         */
        static inline(sourceId, options, layout) {
            options = options || {};
            layout = layout || {};

            var width = (typeof layout.width === "undefined") ? "auto" : layout.width;
            var height = (typeof layout.height === "undefined") ? "auto" : layout.height;
            var scrolling = !!layout.scrolling;

            var idStr = String(sourceId);
            var safeId = idStr.replace(/\\/g, "\\\\").replace(/"/g, '\\"');

            var maxHeightCss = (String(height) === "auto") ? "" : ("max-height:" + String(height) + ";");
            var shellId = "swal-inline-" + idStr;

            var html = '<div data-swal-size="true" style="' + maxHeightCss + '" id="swal-inline-' + safeId + '"></div>';

            var prefersReducedMotion = !!(global.matchMedia && global.matchMedia("(prefers-reduced-motion: reduce)").matches);
            var showClass = prefersReducedMotion ? { popup: "" } : { popup: "swal2-show" };
            var hideClass = prefersReducedMotion ? { popup: "" } : { popup: "swal2-hide" };

            var didOpen = function (el) {
                if (options.background) {
                    var p = el.querySelector(".swal2-popup");
                    if (p) p.style.background = String(options.background);
                }

                var src = document.getElementById(idStr);
                var dest = document.getElementById(shellId);
                if (!dest) dest = el.querySelector("#" + Popup._cssEscape(shellId));

                if (src && dest) dest.innerHTML = src.innerHTML;

                if (scrolling) {
                    var c = el.querySelector(".swal2-html-container");
                    if (c) c.style.overflowY = "auto";
                }

                if (typeof options.didOpen === "function") options.didOpen(el);
            };

            var didClose = function () {
                if (options.background) {
                    var p = document.querySelector(".swal2-popup");
                    if (p) p.style.background = "";
                }
                if (typeof options.didClose === "function") options.didClose();
            };

            var merged = Popup._merge(options, {
                html: html,
                width: width,
                showCloseButton: true,
                closeButtonAriaLabel: "Close dialog",
                showConfirmButton: false,
                buttonsStyling: true,
                allowOutsideClick: true,
                allowEscapeKey: true,
                showClass: showClass,
                hideClass: hideClass,
                didOpen: didOpen,
                didClose: didClose
            });

            return Popup.fire(merged);
        }

        /**
         * @private
         * @param {Object} a
         * @param {Object} b
         * @returns {Object}
         */
        static _merge(a, b) {
            var out = {};
            var k;
            if (a) {
                for (k in a) if (Object.prototype.hasOwnProperty.call(a, k)) out[k] = a[k];
            }
            if (b) {
                for (k in b) if (Object.prototype.hasOwnProperty.call(b, k)) out[k] = b[k];
            }
            return out;
        }

        /**
         * Minimal CSS identifier escape for querySelector.
         * @private
         * @param {string} value
         * @returns {string}
         */
        static _cssEscape(value) {
            return String(value).replace(/([ !"#$%&'()*+,./:;<=>?@[\\\]^`{|}~])/g, "\\$1");
        }
    }

    global.Popup = Popup;
})(window);
